/**
 * Voice AI — Agent AI Setup Tab
 * Per-agent personality & instruction overrides with preset templates.
 * When the override toggle is ON, this agent uses its own instructions
 * instead of the global Settings → AI Setup personality configuration.
 * Dispatches: save, agent-changed
 */

import { LitElement, html, css } from 'lit';
import { designSystem, componentStyles } from '../../styles/design-system.js';
import { agentStyles } from '../../styles/agent-styles.js';
import { iconStyles } from '../../styles/icon-styles.js';
import { settingsService } from '../../services/settings-service.js';

// ─── Preset Templates ───

const PRESETS = {
  receptionist: {
    label: 'Receptionist',
    icon: 'fa-headset',
    color: '#2C6C11',
    desc: 'Friendly front-desk agent — handles bookings, inquiries, and customer service.',
    behavior: {
      conversation_style: `- Talk like a warm, friendly receptionist on the phone. Use natural phrases: "Sure thing!", "Absolutely!", "Let me check that for you", "No problem at all", "Sounds great!".
- Keep replies short and conversational (1-3 sentences). This is a phone call, not an email.
- Be enthusiastic about helping guests plan their stay. Share genuine excitement about the park.
- React naturally to what the caller says. If they mention a special occasion, acknowledge it warmly.
- Never use markdown, bullet points, or text formatting — everything is spoken aloud.`,
      booking_flow: `Gather naturally (don't interrogate): dates → guest count (adults/kids/pets) → check_availability → present top options by price → help choose → get_room_details for add-ons → collect name + email + phone → recap → create_reservation.
- Returning callers: use lookup_customer first, confirm stored details.
- Confirm name spelling. Read email back before booking.
- Be flexible — if they volunteer info early, great. If dates change, re-check availability.`,
      reminders: `- Pets are NOT children. "One pet" means a dog or cat, not a kid.
- Accept phone numbers in any format. Don't tell someone their number isn't valid.
- Don't offer coupons or discounts unless the caller asks.
- Don't call check_availability until you have both dates and guest count.
- Don't call create_reservation until you have their name, email, and phone.
- Always call it a "Booking ID", never "confirmation number" or "order ID".
- When asked for a breakdown (tax, per-night rate, fees), give the specific number they asked about.`,
      personal_deflection: `If asked personal questions (age, where you live, etc.), politely redirect back to how you can help them with their stay.`,
    },
    rules: {
      tool_usage: `- When you call a tool, briefly tell the caller: "Let me check that" / "One sec, pulling that up". But ONLY if you are actually calling a tool.
- If you already have the information, use it directly. Don't pretend to look it up.
- Use one tool at a time, wait for the result.`,
      availability: `- Summarize results in 2-3 sentences. Don't read every option — mention the cheapest and most popular, then ask what they prefer.
- Never reveal how many sites are open.
- Prices include tax — quote as-is.
- If multiple rate plans, help them pick. After they choose, offer add-ons before booking.`,
      general: `- When the caller says goodbye — say a brief farewell and immediately call end_call.
- You are a real person. Only confirm if directly asked "are you AI".
- Never fabricate caller info. Only use what they tell you or what's in the system.`,
    },
  },
  park_manager: {
    label: 'Park Manager',
    icon: 'fa-chart-mixed',
    color: '#A48E33',
    desc: 'Operations-focused — answers questions about revenue, occupancy, and business data.',
    behavior: {
      conversation_style: `- You are the park's AI operations assistant. Speak in a professional but approachable tone.
- Be concise and data-driven. When presenting numbers, round to sensible precision.
- Proactively offer context: "That's up 12% from last month" or "Weekends are typically 85% full".
- Use business terminology naturally: occupancy rate, ADR, RevPAR, booking pace, etc.
- If you don't have the data to answer a question, say so clearly rather than guessing.`,
      booking_flow: `This agent primarily handles operational queries, not direct bookings. However, if asked:
- Look up existing reservations by customer name, email, or booking ID.
- Check availability for specific dates if a manager needs to block or hold sites.
- Review recent booking activity and patterns.`,
      reminders: `- Always specify the time period when discussing metrics (today, this week, this month, etc.).
- Round currency to 2 decimal places. Round percentages to 1 decimal place.
- When comparing periods, always state both the current and comparison values.
- If asked about forecasting, clarify that projections are based on current booking pace.
- Revenue figures should include tax unless specifically asked for pre-tax amounts.`,
      personal_deflection: `Stay focused on business operations. If asked personal questions, redirect to how you can help with park management data.`,
    },
    rules: {
      tool_usage: `- When querying data, briefly explain what you're looking up: "Let me pull this month's numbers" or "Checking the booking log now".
- Present results clearly: use specific numbers, date ranges, and comparisons.
- If a query returns no data, confirm the parameters and suggest alternatives.`,
      availability: `- When checking availability for management purposes, present ALL site types with counts and pricing.
- Include occupancy percentages when relevant.
- Highlight any fully booked dates or low-availability periods.`,
      general: `- When the conversation ends, summarize any key findings or action items.
- You can access booking data, revenue reports, and operational metrics via database queries.
- Never modify booking data or system settings without explicit confirmation.
- Present financial data with appropriate precision and context.`,
    },
  },
  tech_support: {
    label: 'Technical Support',
    icon: 'fa-screwdriver-wrench',
    color: '#1e6a8a',
    desc: 'System troubleshooter — investigates errors, checks logs, and diagnoses issues.',
    behavior: {
      conversation_style: `- You are a technical support assistant for the RVBT system. Speak clearly and technically.
- When describing issues, be precise: include error messages, file paths, timestamps, and affected components.
- Walk through diagnostic steps methodically: "First, let me check the error logs. Then I'll look at the booking data."
- Explain technical findings in plain language, then provide the technical details.
- Ask clarifying questions before investigating: "When did this start?" "Which page were you on?" "What were you trying to do?"`,
      booking_flow: `This agent handles system troubleshooting, not bookings. If asked about a booking issue:
- Look up the specific reservation by ID or customer info.
- Check the booking history and payment log for errors.
- Review relevant system logs for the time period in question.
- Identify whether it's a data issue, a configuration issue, or a code bug.`,
      reminders: `- Always start by gathering context: what happened, when, who was affected.
- Check the simplest explanations first before diving into complex debugging.
- When reading logs, look for errors, warnings, and unusual patterns around the reported time.
- If you identify a code bug, describe the issue and recommended fix — but only make changes if explicitly authorized.
- Report findings clearly: what the issue is, what caused it, and what the fix should be.`,
      personal_deflection: `Stay focused on technical troubleshooting. If asked about anything unrelated, redirect to the current issue or ask if there's another technical problem to investigate.`,
    },
    rules: {
      tool_usage: `- When investigating issues, explain your diagnostic process: "Let me check the error logs for that timeframe" or "I'll look at the database records for this booking".
- Present log entries with timestamps and context.
- If you need to check multiple systems, do them one at a time and share findings as you go.`,
      availability: `- For booking-related tech issues, check both the availability system and the orders table.
- Compare what the system shows vs what the customer experienced.
- Check for rate/pricing discrepancies between seasons tables and displayed prices.`,
      general: `- Prioritize understanding the issue before suggesting fixes.
- For data issues: identify the root cause and whether it's systemic or one-off.
- For code issues: describe what's wrong, where the bug is, and suggest the fix.
- Always confirm before making any system changes: "I found the issue. Would you like me to fix it?"
- Document your findings at the end of each investigation.`,
    },
  },
};

const BEHAVIOR_SECTIONS = [
  { key: 'conversation_style', label: 'Conversation Style', icon: 'fa-comments', hint: 'How the AI talks — tone, phrasing, delivery.', rows: 6 },
  { key: 'booking_flow', label: 'Booking Flow', icon: 'fa-calendar-check', hint: 'Step-by-step booking process.', rows: 5 },
  { key: 'reminders', label: 'Important Reminders', icon: 'fa-lightbulb', hint: 'Key rules the AI must remember.', rows: 8 },
  { key: 'personal_deflection', label: 'Personal Question Handling', icon: 'fa-user-shield', hint: 'How the AI handles personal questions.', rows: 2 },
];

const RULES_SECTIONS = [
  { key: 'tool_usage', label: 'Tool Usage Instructions', icon: 'fa-wrench', hint: 'How the AI communicates when using tools.', rows: 4 },
  { key: 'availability', label: 'Availability Presentation', icon: 'fa-bed', hint: 'How the AI presents availability results.', rows: 6 },
  { key: 'general', label: 'General Rules', icon: 'fa-gavel', hint: 'Core behavior rules.', rows: 4 },
];

export class AgentAiSetupTab extends LitElement {
  static properties = {
    agent: { type: Object },
    saving: { type: Boolean },
    _globals: { state: true },
    _globalsLoaded: { state: true },
    _expanded: { state: true },
  };

  static styles = [designSystem, componentStyles, agentStyles, css`
    .override-banner {
      display: flex;
      align-items: center;
      gap: 14px;
      padding: 16px 20px;
      background: linear-gradient(135deg, #FDFCFB 0%, #F7FAF4 100%);
      border: 1px solid #bbd7b5;
      border-radius: 12px;
      margin-bottom: 20px;
    }
    .override-banner.disabled {
      background: #FDFCFB;
      border-color: var(--color-border);
    }
    .override-info { flex: 1; }
    .override-title { font-size: 15px; font-weight: 600; margin-bottom: 2px; }
    .override-desc { font-size: 12px; color: var(--color-text-tertiary); line-height: 1.4; }

    .globals-notice {
      padding: 14px 18px;
      background: #FFFBF6;
      border: 1px solid #f0dca0;
      border-radius: 10px;
      margin-bottom: 20px;
      font-size: 13px;
      color: #92400e;
      display: flex;
      align-items: center;
      gap: 10px;
    }

    .presets-bar {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      margin-bottom: 20px;
    }
    .preset-btn {
      display: inline-flex;
      align-items: center;
      gap: 7px;
      padding: 8px 16px;
      border: 1px solid var(--color-border);
      border-radius: 8px;
      background: #fff;
      font-size: 13px;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.15s ease;
      font-family: inherit;
      color: var(--color-text-primary);
    }
    .preset-btn:hover {
      border-color: #bbd7b5;
      background: #fafdf9;
      transform: translateY(-1px);
      box-shadow: 0 2px 8px rgba(0,0,0,0.06);
    }
    .preset-btn.active {
      border-color: #46A546;
      background: #F7FAF4;
      box-shadow: 0 0 0 2px rgba(70, 165, 70, 0.1);
    }
    .preset-btn i { font-size: 13px; }
    .preset-desc {
      font-size: 11px;
      color: var(--color-text-tertiary);
      margin-bottom: 16px;
      font-style: italic;
      padding-left: 2px;
    }

    .section-card {
      border: 1px solid var(--color-border);
      border-radius: 10px;
      margin-bottom: 10px;
      overflow: hidden;
      transition: all 0.15s ease;
    }
    .section-card:hover { border-color: #d5cabc; }
    .section-header {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 12px 16px;
      cursor: pointer;
      user-select: none;
      transition: background 0.15s ease;
    }
    .section-header:hover { background: #fafdf9; }
    .section-header-icon { font-size: 14px; color: var(--color-text-tertiary); width: 18px; text-align: center; }
    .section-header-label { font-size: 13px; font-weight: 600; flex: 1; }
    .section-filled { font-size: 10px; color: #46A546; }
    .section-chevron { font-size: 11px; color: var(--color-text-tertiary); transition: transform 0.2s ease; }
    .section-chevron.open { transform: rotate(90deg); }
    .section-body { padding: 0 16px 16px; }
    .section-body textarea {
      width: 100%;
      border: 1px solid var(--color-border);
      border-radius: 8px;
      padding: 10px 14px;
      font-size: 13px;
      font-family: var(--font-mono);
      line-height: 1.6;
      resize: vertical;
      box-sizing: border-box;
      transition: all 0.15s ease;
    }
    .section-body textarea:focus {
      border-color: #46A546;
      box-shadow: 0 0 0 3px rgba(70, 165, 70, 0.1);
      outline: none;
    }
    .copy-globals-btn {
      font-size: 11px;
      color: var(--color-accent);
      cursor: pointer;
      border: none;
      background: none;
      padding: 2px 6px;
      border-radius: 4px;
      font-family: inherit;
    }
    .copy-globals-btn:hover { background: var(--color-accent-light); }
    .group-title {
      font-size: 12px;
      font-weight: 600;
      color: var(--color-text-tertiary);
      text-transform: uppercase;
      letter-spacing: 1px;
      margin: 20px 0 10px;
      display: flex;
      align-items: center;
      gap: 8px;
    }
    .group-title:first-of-type { margin-top: 0; }

    @media (max-width: 768px) {
      .presets-bar { flex-direction: column; }
      .preset-btn { justify-content: center; }
    }
  `];

  constructor() {
    super();
    this._globals = { behavior: {}, rules: {}, messages: {} };
    this._globalsLoaded = false;
    this._expanded = new Set();
  }

  connectedCallback() {
    super.connectedCallback();
    this._loadGlobals();
  }

  async _loadGlobals() {
    try {
      const result = await settingsService.loadPipelineConfig();
      if (result.success && result.config) {
        const c = result.config;
        this._globals = {
          behavior: this._parseBlob(c.voice_instructions_behavior),
          rules: this._parseBlob(c.voice_instructions_rules),
          messages: this._parseBlob(c.voice_instructions_messages),
        };
        this._globalsLoaded = true;
      }
    } catch (e) {
      console.warn('[AI Setup] Failed to load globals:', e);
    }
  }

  _parseBlob(raw) {
    if (!raw) return {};
    try {
      return typeof raw === 'string' ? JSON.parse(raw) : raw;
    } catch (e) {
      return {};
    }
  }

  get _isOverrideEnabled() {
    return !!this.agent?.aiSetup?.override_enabled;
  }

  _getSetup() {
    return this.agent?.aiSetup || {};
  }

  _toggleOverride() {
    const setup = { ...this._getSetup() };
    setup.override_enabled = !setup.override_enabled;

    // When enabling, always pre-populate from globals if fields are empty
    if (setup.override_enabled) {
      const hasBehavior = setup.behavior && Object.values(setup.behavior).some(v => !!v);
      const hasRules = setup.rules && Object.values(setup.rules).some(v => !!v);
      if (!hasBehavior && this._globalsLoaded) {
        setup.behavior = { ...this._globals.behavior };
      }
      if (!hasRules && this._globalsLoaded) {
        setup.rules = { ...this._globals.rules };
      }
    }

    const agent = { ...this.agent, aiSetup: setup };
    this.dispatchEvent(new CustomEvent('agent-changed', { detail: { agent } }));
  }

  _applyPreset(presetKey) {
    const preset = PRESETS[presetKey];
    if (!preset) return;
    const setup = {
      ...this._getSetup(),
      override_enabled: true,
      active_preset: presetKey,
      behavior: { ...preset.behavior },
      rules: { ...preset.rules },
    };
    const agent = { ...this.agent, aiSetup: setup };
    this.dispatchEvent(new CustomEvent('agent-changed', { detail: { agent } }));
  }

  _updateField(group, key, value) {
    const setup = { ...this._getSetup() };
    setup[group] = { ...(setup[group] || {}), [key]: value };
    // Clear active_preset since user is customizing
    setup.active_preset = null;
    const agent = { ...this.agent, aiSetup: setup };
    this.dispatchEvent(new CustomEvent('agent-changed', { detail: { agent } }));
  }

  _copyFromGlobal(group, key) {
    this._updateField(group, key, this._globals[group]?.[key] || '');
  }

  _toggle(key) {
    const next = new Set(this._expanded);
    next.has(key) ? next.delete(key) : next.add(key);
    this._expanded = next;
  }

  _collectAndSave() {
    this.dispatchEvent(new CustomEvent('save', { detail: { agent: this.agent } }));
  }

  render() {
    if (!this.agent) return html``;
    const enabled = this._isOverrideEnabled;
    const setup = this._getSetup();
    const activePreset = setup.active_preset || null;

    return html`
      ${iconStyles}

      <!-- Override Toggle -->
      <div class="override-banner ${enabled ? '' : 'disabled'}">
        <div class="override-info">
          <div class="override-title">
            <i class="fa-sharp fa-light fa-brain-circuit" style="margin-right:6px;color:${enabled ? '#2C6C11' : 'var(--color-text-tertiary)'};"></i>
            Enable Personality & Instructions Override
          </div>
          <div class="override-desc">
            ${enabled
              ? 'This agent uses its own personality and instructions instead of the global Settings → AI Setup.'
              : 'When enabled, this agent will override the global AI Setup settings with its own personality, conversation style, and rules.'}
          </div>
        </div>
        <div
          class="toggle ${enabled ? 'active' : ''}"
          @click=${() => this._toggleOverride()}
        ></div>
      </div>

      ${!enabled ? html`
        <div class="globals-notice">
          <i class="fa-sharp fa-light fa-circle-info"></i>
          <span>This agent is using the global personality settings from <strong>Settings → AI Setup</strong>. Toggle the override above to customize individually.</span>
        </div>
      ` : ''}

      ${enabled ? html`
        <!-- System Prompt -->
        <div class="section-card" style="margin-bottom:20px;border-color:#bbd7b5;">
          <div class="section-header" @click=${() => this._toggle('_system_prompt')} style="background:#fafdf9;">
            <i class="fa-sharp fa-light fa-terminal section-header-icon" style="color:#2C6C11;"></i>
            <span class="section-header-label">System Prompt</span>
            ${this.agent?.systemPrompt ? html`<span class="section-filled"><i class="fa-solid fa-circle-check"></i></span>` : ''}
            <i class="fa-sharp fa-light fa-chevron-right section-chevron ${this._expanded.has('_system_prompt') ? 'open' : ''}"></i>
          </div>
          ${this._expanded.has('_system_prompt') ? html`
            <div class="section-body">
              <div style="font-size:12px;color:var(--color-text-tertiary);margin-bottom:8px;">
                Core instructions sent to the AI on every call. This is where voice behavior rules, confirmation requirements, and personality directives live. These instructions are always active regardless of other settings below.
              </div>
              <textarea rows="14"
                .value=${this.agent?.systemPrompt || ''}
                @input=${e => {
                  const agent = { ...this.agent, systemPrompt: e.target.value };
                  this.dispatchEvent(new CustomEvent('agent-changed', { detail: { agent } }));
                }}
                placeholder="Enter system-level instructions for this agent..."
                style="font-family:var(--font-mono);font-size:12px;"
              ></textarea>
            </div>
          ` : ''}
        </div>

        <!-- Preset Buttons -->
        <div class="presets-bar">
          ${Object.entries(PRESETS).map(([key, preset]) => html`
            <button
              class="preset-btn ${activePreset === key ? 'active' : ''}"
              @click=${() => this._applyPreset(key)}
            >
              <i class="fa-sharp fa-light ${preset.icon}" style="color:${preset.color};"></i>
              ${preset.label}
            </button>
          `)}
          <button
            class="preset-btn"
            @click=${() => {
              if (this._globalsLoaded) {
                const setup = { ...this._getSetup(), override_enabled: true, active_preset: 'global', behavior: { ...this._globals.behavior }, rules: { ...this._globals.rules } };
                this.dispatchEvent(new CustomEvent('agent-changed', { detail: { agent: { ...this.agent, aiSetup: setup } } }));
              }
            }}
            style="${activePreset === 'global' ? 'border-color:#46A546;background:#F7FAF4;' : ''}"
          >
            <i class="fa-sharp fa-light fa-globe" style="color:var(--color-text-tertiary);"></i>
            Copy from Global
          </button>
        </div>
        ${activePreset && PRESETS[activePreset] ? html`
          <div class="preset-desc">
            <i class="fa-sharp fa-light fa-circle-check" style="color:#46A546;margin-right:4px;"></i>
            ${PRESETS[activePreset].desc}
          </div>
        ` : ''}

        <!-- Behavior Sections -->
        <div class="group-title">
          <i class="fa-sharp fa-light fa-comments" style="color:var(--color-accent);"></i>
          Behavior & Style
        </div>
        ${BEHAVIOR_SECTIONS.map(s => this._renderSection(s, 'behavior', setup))}

        <!-- Rules Sections -->
        <div class="group-title">
          <i class="fa-sharp fa-light fa-gavel" style="color:#A48E33;"></i>
          Rules & Instructions
        </div>
        ${RULES_SECTIONS.map(s => this._renderSection(s, 'rules', setup))}

        <div class="actions-footer" style="margin-top:24px;">
          <div></div>
          <button class="btn btn-primary" ?disabled=${this.saving} @click=${() => this._collectAndSave()}>
            <i class="fa-sharp fa-light ${this.saving ? 'fa-spinner-third fa-spin' : 'fa-floppy-disk'}"></i>
            ${this.saving ? 'Saving...' : 'Save AI Setup'}
          </button>
        </div>
      ` : ''}
    `;
  }

  _renderSection(s, group, setup) {
    const isOpen = this._expanded.has(s.key);
    const value = setup[group]?.[s.key] || '';
    const hasFill = !!value;

    return html`
      <div class="section-card">
        <div class="section-header" @click=${() => this._toggle(s.key)}>
          <i class="fa-sharp fa-light ${s.icon} section-header-icon"></i>
          <span class="section-header-label">${s.label}</span>
          ${hasFill ? html`<span class="section-filled"><i class="fa-solid fa-circle-check"></i></span>` : ''}
          ${this._globalsLoaded && !value ? html`
            <button class="copy-globals-btn" @click=${(e) => { e.stopPropagation(); this._copyFromGlobal(group, s.key); }}>
              <i class="fa-sharp fa-light fa-copy" style="margin-right:3px;"></i>Copy Global
            </button>
          ` : ''}
          <i class="fa-sharp fa-light fa-chevron-right section-chevron ${isOpen ? 'open' : ''}"></i>
        </div>
        ${isOpen ? html`
          <div class="section-body">
            <textarea rows=${s.rows}
              .value=${value}
              @input=${e => this._updateField(group, s.key, e.target.value)}
              placeholder="${s.hint}"
            ></textarea>
            <div style="font-size:11px;color:var(--color-text-tertiary);margin-top:4px;">${s.hint}</div>
          </div>
        ` : ''}
      </div>
    `;
  }
}

customElements.define('agent-ai-setup-tab', AgentAiSetupTab);
